import type { TileSize } from '../../components/TileSizeControl.tsx';

/**
 * The dashboard's view, as ONE object.
 *
 * Everything the homepage redesign adds — filter pills, sort, the view
 * switcher, the sidebar's Views section, the active-filter chips — reads and
 * writes this shape, so it lives apart from React: parsing and serialising are
 * pure functions with tests, and the hook next door only owns the wiring.
 *
 * The object is mirrored into the query string, which is the point of it: a
 * filtered dashboard can be copied, bookmarked, reloaded and sent to someone
 * else. That only works if the mapping is total in both directions, so every
 * axis here has a short, stable param name and a default that is OMITTED from
 * the URL — a dashboard nobody has filtered has a clean address bar.
 */

// Sentinel for the "Uncategorised" node — a leading-NUL string that can't
// collide with a real category path (paths are trimmed, non-empty segments).
// It lives here rather than in useDeckFilters because the URL codec has to
// know it: `cat=uncategorised` is its wire form.
export const UNCATEGORISED = ' uncategorised';

/** The "whoever is reading this" owner. Not a person id, and deliberately not
 *  resolved into one: see `DeckFilterState.owner`. */
export const ME = 'me';

/** The exclusive status axis. `null` is All — published/unpublished narrow the
 *  grid, trash REPLACES it. */
export type DeckStatusFilter = null | 'published' | 'unpublished' | 'trash';

/** How the grid is ordered. `edited` is the server's own order (newest edit
 *  first) and the default, so nothing moves until someone asks it to. */
export type DeckSort = 'edited' | 'opened' | 'name' | 'slides';

/** Which body renders the decks. Only `grid` exists today. */
export type DeckViewMode = 'grid' | 'rows' | 'list';

/** "Edited in the last …". `null` is any time. */
export type UpdatedWindow = null | 'today' | 'week' | 'month' | 'quarter';

/* An `opened` narrowing axis (`?opened=recent`, a 14-day window) lived here
 * from Run 4 until 2026-09-07. It existed to back the rail's Recently opened
 * row and nothing else ever offered it, so it was retired with that row: "when
 * did I last open this" is answered by the Sort pill's `opened` value and by
 * the Jump back in strip, neither of which pretends to be a filter. `sort`
 * below still carries it. */

export type DeckFilterState = {
  query: string;
  status: DeckStatusFilter;
  /** A category path, the UNCATEGORISED sentinel, or null for all. */
  category: string | null;
  /** A model id from `deckModelId`, matched against `DeckSummary.builtBy`. */
  model: string | null;
  /** A people.json id, the ME sentinel, or null for anyone's. The sentinel is
   *  stored rather than resolved so that a copied link says "your decks" to
   *  whoever opens it — which is what "My decks" means, and what a resolved id
   *  would quietly turn into "Alex's decks". Resolving happens at the point of
   *  comparison, in useDeckFilters. */
  owner: string | null;
  updated: UpdatedWindow;
  sort: DeckSort;
  view: DeckViewMode;
  /** Nested inside the grid view: table and list have no size. */
  tileSize: TileSize;
};

export const DEFAULT_FILTER_STATE: DeckFilterState = {
  query: '',
  status: null,
  category: null,
  model: null,
  owner: null,
  updated: null,
  sort: 'edited',
  view: 'grid',
  tileSize: 'md',
};

/** Param names. Short because they end up in links people paste to each
 *  other; frozen because those links outlive the code that wrote them. */
export const FILTER_PARAMS = {
  query: 'q',
  status: 'status',
  category: 'cat',
  model: 'model',
  owner: 'owner',
  updated: 'updated',
  sort: 'sort',
  view: 'view',
  tileSize: 'size',
} as const;

/** The dashboard shipped `?category=` before this object existed and
 *  `onCopyFilterLink` handed those links out, so the old name is still READ.
 *  It is never written: an old link normalises to `cat` on arrival. */
const LEGACY_CATEGORY_PARAM = 'category';

/** The wire form of the Uncategorised bucket — the sentinel is unprintable. */
const UNCATEGORISED_PARAM = 'uncategorised';

const STATUSES: readonly Exclude<DeckStatusFilter, null>[] = ['published', 'unpublished', 'trash'];
const SORTS: readonly DeckSort[] = ['edited', 'opened', 'name', 'slides'];
const VIEWS: readonly DeckViewMode[] = ['grid', 'rows', 'list'];
const WINDOWS: readonly Exclude<UpdatedWindow, null>[] = ['today', 'week', 'month', 'quarter'];
const TILE_SIZES: readonly TileSize[] = ['sm', 'md', 'lg'];

/** Junk in a param is a bad link, not a broken page: anything unrecognised
 *  falls back to the default rather than throwing or rendering an empty grid. */
function oneOf<T extends string>(raw: string | null, allowed: readonly T[], fallback: T | null): T | null {
  if (!raw) return fallback;
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

/**
 * Read the view out of a query string.
 *
 * `defaults` is how the tile size stays a personal preference rather than part
 * of the shared view: the caller passes the value saved in localStorage, so an
 * absent `size` means "whatever this browser last chose", while a link that
 * carries one still wins.
 */
export function parseFilterState(
  params: URLSearchParams,
  defaults: DeckFilterState = DEFAULT_FILTER_STATE,
): DeckFilterState {
  const rawCat = params.get(FILTER_PARAMS.category) ?? params.get(LEGACY_CATEGORY_PARAM);
  const cat = rawCat ? (rawCat === UNCATEGORISED_PARAM ? UNCATEGORISED : rawCat) : defaults.category;
  return {
    query: params.get(FILTER_PARAMS.query) ?? defaults.query,
    status: oneOf(params.get(FILTER_PARAMS.status), STATUSES, defaults.status),
    category: cat,
    model: params.get(FILTER_PARAMS.model) || defaults.model,
    owner: params.get(FILTER_PARAMS.owner) || defaults.owner,
    updated: oneOf(params.get(FILTER_PARAMS.updated), WINDOWS, defaults.updated),
    sort: oneOf(params.get(FILTER_PARAMS.sort), SORTS, defaults.sort) ?? DEFAULT_FILTER_STATE.sort,
    view: oneOf(params.get(FILTER_PARAMS.view), VIEWS, defaults.view) ?? DEFAULT_FILTER_STATE.view,
    tileSize: oneOf(params.get(FILTER_PARAMS.tileSize), TILE_SIZES, defaults.tileSize) ?? DEFAULT_FILTER_STATE.tileSize,
  };
}

/**
 * Write the view back into a query string.
 *
 * `base` carries any params that aren't ours (nothing uses one today, but the
 * old code preserved them and losing someone else's param on a filter click is
 * the kind of bug you find months later). A value equal to its default is
 * DELETED, not written — that is what keeps a fresh dashboard's address bar
 * clean and a shared link down to the axes that were actually set.
 */
export function filterStateToParams(
  state: DeckFilterState,
  base?: URLSearchParams,
  defaults: DeckFilterState = DEFAULT_FILTER_STATE,
): URLSearchParams {
  const next = new URLSearchParams(base);
  next.delete(LEGACY_CATEGORY_PARAM); // an old link normalises to `cat`
  const put = (key: string, value: string | null, isDefault: boolean) => {
    if (isDefault || !value) next.delete(key);
    else next.set(key, value);
  };
  put(FILTER_PARAMS.query, state.query, state.query === defaults.query);
  put(FILTER_PARAMS.status, state.status, state.status === defaults.status);
  put(
    FILTER_PARAMS.category,
    state.category === UNCATEGORISED ? UNCATEGORISED_PARAM : state.category,
    state.category === defaults.category,
  );
  put(FILTER_PARAMS.model, state.model, state.model === defaults.model);
  put(FILTER_PARAMS.owner, state.owner, state.owner === defaults.owner);
  put(FILTER_PARAMS.updated, state.updated, state.updated === defaults.updated);
  put(FILTER_PARAMS.sort, state.sort, state.sort === defaults.sort);
  put(FILTER_PARAMS.view, state.view, state.view === defaults.view);
  put(FILTER_PARAMS.tileSize, state.tileSize, state.tileSize === defaults.tileSize);
  return next;
}

/** The axes that NARROW the deck list. Sort, view and tile size describe how
 *  the same decks are shown, so they are presentation, not filters, and the
 *  chip row must not offer to clear them. */
const FILTER_AXES = ['query', 'status', 'category', 'model', 'updated', 'owner'] as const;

/** How many filters are on — the number beside "Clear all". */
export function activeFilterCount(state: DeckFilterState): number {
  return FILTER_AXES.filter((axis) => state[axis] !== DEFAULT_FILTER_STATE[axis]).length;
}

/** "Clear all": drop every filter, keep how the user likes things shown. */
export function clearedFilterState(state: DeckFilterState): DeckFilterState {
  return { ...state, query: '', status: null, category: null, model: null, updated: null, owner: null };
}
